import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const SpanishColonialADUDesignBelmont = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Spanish Colonial ADU Design & Construction in Belmont",
    "description": "Create authentic Spanish Colonial ADUs in Belmont featuring stucco walls, tile roofs, and Mediterranean-inspired courtyards.",
    "url": "https://www.udaadu.com/spanish-colonial-adu-design-belmont",
    "mainEntity": {
      "@type": "Service",
      "name": "Spanish Colonial ADU Design & Construction",
      "provider": {
        "@type": "Organization",
        "name": "UDA ADU"
      },
      "areaServed": "Belmont, CA"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/spanish-colonial-adu-design-belmont" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
            Spanish Colonial ADU Design & Construction in Belmont
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Create authentic Spanish Colonial ADUs in Belmont featuring stucco walls, tile roofs, and Mediterranean-inspired courtyards.
          </p>
        </div>

        {/* Call to Action */}
        <div className="text-center mb-16">
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Get Free Consultation
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/adu-calculator">Calculate Costs</Link>
          </Button>
        </div>

        {/* Why Choose Spanish Colonial ADUs */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Why Choose Spanish Colonial ADUs in Belmont?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">California Heritage</h3>
              <p className="text-muted-foreground">
                Spanish Colonial architecture reflects California's rich cultural heritage, bringing authentic historical character to Belmont properties.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Climate Synergy</h3>
              <p className="text-muted-foreground">
                Designed for Mediterranean climates, Spanish Colonial features like courtyards and covered patios perfectly suit Belmont's weather patterns.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Elegant Simplicity</h3>
              <p className="text-muted-foreground">
                Clean stucco walls and red tile roofs create striking yet understated elegance that enhances property value and curb appeal.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Spanish Colonial ADU Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-foreground">Exterior Elements</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Smooth stucco walls in warm earth tones</li>
                <li>• Red clay tile roofs with low pitches</li>
                <li>• Arched doorways and windows</li>
                <li>• Wrought iron details and balconies</li>
                <li>• Private courtyards and covered patios</li>
              </ul>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-foreground">Interior Design</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Terra cotta or ceramic tile flooring</li>
                <li>• Exposed wooden beam ceilings</li>
                <li>• Arched openings and built-in niches</li>
                <li>• Natural stone and tile accents</li>
                <li>• Warm color schemes and textures</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Local Considerations */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Belmont Building Considerations</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Architectural Harmony</h3>
              <p className="text-muted-foreground">
                Spanish Colonial ADUs complement Belmont's diverse architectural styles while adding distinctive character that respects neighborhood context.
              </p>
            </div>
            <div className="p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Sustainable Materials</h3>
              <p className="text-muted-foreground">
                Traditional materials like stucco and tile provide excellent durability and energy efficiency in Belmont's moderate coastal climate.
              </p>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Investment Range</h2>
          <div className="text-center p-8 border border-border rounded-lg">
            <p className="text-2xl font-bold mb-4 text-foreground">$260,000 - $420,000</p>
            <p className="text-muted-foreground">
              Spanish Colonial ADUs feature authentic materials and craftsmanship. Courtyard spaces and custom details enhance value and livability.
            </p>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-8 text-foreground">Start Your Spanish Colonial ADU Project</h2>
          <p className="text-xl mb-8 text-muted-foreground max-w-3xl mx-auto">
            Ready to bring Spanish Colonial elegance to your Belmont property? Let's create your perfect Mediterranean-inspired ADU.
          </p>
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Schedule Consultation
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/adu-calculator">Get Cost Estimate</Link>
          </Button>
        </section>

        {/* Internal Links */}
        <section className="text-center">
          <h3 className="text-2xl font-semibold mb-6 text-foreground">Explore Other ADU Styles</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild variant="outline">
              <Link to="/mediterranean-adu-design-belmont">Mediterranean ADUs</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/mission-style-adu-design-belmont">Mission Style ADUs</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/tuscan-adu-design-belmont">Tuscan ADUs</Link>
            </Button>
          </div>
        </section>
      </main>

      <FloatingADUWizard />
      <FloatingReserveButton />
      <Footer />
    </div>
  );
};

export default SpanishColonialADUDesignBelmont;